import { useEffect, useRef, useState } from 'react';
import { Check, Copy, KeyRound, Loader2 } from 'lucide-react';
import { apiErrorCode } from '@/lib/api';
import { useResetLink, type ResetLinkKind } from '@/hooks/useResetLink';

interface ResetLinkDialogProps {
  kind: ResetLinkKind;
  id: number;
  name: string;
  onClose: () => void;
}

const WHO: Record<ResetLinkKind, string> = {
  participant: 'à própria pessoa ou ao seu Encarregado de Educação',
  guardian: 'ao próprio Encarregado de Educação',
  staff: 'à própria pessoa',
};

function formatExpiry(iso: string): string {
  return new Date(iso).toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errorMessage(err: unknown): string {
  if (apiErrorCode(err) === 'ACCOUNT_NOT_ACTIVE') {
    return 'Esta conta não está ativa. Ativa-a antes de gerar uma ligação.';
  }
  return 'Não foi possível gerar a ligação. Tenta outra vez.';
}

/**
 * Generates a password reset link for staff to hand over themselves. The app
 * sends no email, so this is the only way back in for someone who forgot.
 */
export default function ResetLinkDialog({ kind, id, name, onClose }: ResetLinkDialogProps) {
  const resetLink = useResetLink();
  const [copied, setCopied] = useState(false);
  const linkRef = useRef<HTMLInputElement>(null);
  const link = resetLink.data;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !resetLink.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, resetLink.isPending]);

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
    } catch {
      // No clipboard access (plain http, old browser): select it for Ctrl+C.
      linkRef.current?.select();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-link-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-primary/10">
            <KeyRound className="w-5 h-5 text-primary" aria-hidden="true" />
          </div>
          <div className="space-y-2 text-sm text-muted-foreground min-w-0">
            <h3 id="reset-link-title" className="text-base font-semibold text-foreground">
              Ligação de recuperação
            </h3>
            {!link && (
              <p>
                Gera uma ligação para <span className="font-medium text-foreground">{name}</span> definir
                uma nova palavra-passe. Entrega-a só {WHO[kind]}: quem a tiver pode mudar a
                palavra-passe. Uma ligação nova anula a anterior.
              </p>
            )}
            {link && (
              <>
                <p>
                  Envia ou mostra esta ligação {WHO[kind]}. Funciona uma vez, até{' '}
                  <span className="font-medium text-foreground">{formatExpiry(link.expiresAt)}</span>.
                </p>
                <figure className="flex flex-col items-center gap-2 py-1">
                  <img
                    src={link.qrCode}
                    alt={`Código QR da ligação de recuperação de ${name}`}
                    width={208}
                    height={208}
                    className="rounded-md border border-border bg-white [image-rendering:pixelated]"
                  />
                  <figcaption className="text-xs">Aponta a câmara do telemóvel para abrir a ligação.</figcaption>
                </figure>
                <div className="flex gap-2">
                  <label htmlFor="reset-link-url" className="sr-only">
                    Ligação de recuperação
                  </label>
                  <input
                    id="reset-link-url"
                    ref={linkRef}
                    readOnly
                    value={link.url}
                    onFocus={(e) => e.currentTarget.select()}
                    className="input flex-1 min-w-0 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={copy}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copiada' : 'Copiar'}
                  </button>
                </div>
              </>
            )}
            {resetLink.isError && (
              <p className="text-destructive" role="alert">
                {errorMessage(resetLink.error)}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={resetLink.isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            {link ? 'Fechar' : 'Voltar'}
          </button>
          {!link && (
            <button
              type="button"
              onClick={() => resetLink.mutate({ kind, id })}
              disabled={resetLink.isPending}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {resetLink.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Gerar ligação
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
